import classNames from 'classnames';
import { useEffect } from 'react';

import { Homeless } from 'types';

import Card from '../../Card';

import { useDebitCardActions } from 'hooks/useDebitCardActions';

import { renderCardType } from './utils';

import Button from '@mui/material/Button';
import { createStyles, makeStyles } from '@mui/styles';

const useStyles = makeStyles(() =>
    createStyles({
        button: {
            display: 'flex',
            alignItems: 'center',
            flex: 'none',
            flexGrow: 0,
            gap: '8px',
            borderRadius: '20px',
            boxSizing: 'border-box',
            fontWeight: 800,
            height: '40px',
            order: 0,
            padding: '0px 32px',
            textTransform: 'none'
        },
        buttonCancel: {
            border: '1px solid #A80A2D',
            color: '#A80A2D'
        },
        buttonClose: {
            backgroundColor: '#A80A2D',
            color: '#FFFFFF'
        },
        buttonContainer: {
            display: 'flex',
            justifyContent: 'space-between'
        },
        cardStyle: {
            margin: '0px !important'
        },
        closeCardContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: '40px'
        },
        title: {
            color: '#A80A2D',
            fontWeight: 800,
            marginBottom: '0',
            textAlign: 'center'
        }
    })
);

type CardCloseProps = {
    holder: Homeless;
    onClose: () => void;
    setLoading?: (loading: boolean) => void;
    setOverlayError?: (message: string | null) => void;
    setSuccess?: (success: boolean) => void;
};

const OVERLAY_ERROR_RESET_MS = 5000;

const CardClose: React.FC<CardCloseProps> = ({
    holder,
    onClose,
    setLoading,
    setOverlayError,
    setSuccess
}) => {
    const classes = useStyles();

    const {
        closeDebitCard,
        debitInfo,
        error,
        errorMessage,
        loading,
        resetDebitState,
        success
    } = useDebitCardActions();

    const { id } = holder;
    const { card_last4, card_type } = debitInfo || {
        card_last4: null,
        card_type: null
    };

    const cancelButtonClass = classNames(classes.button, classes.buttonCancel);
    const closeButtonClass = classNames(classes.button, classes.buttonClose);

    const handleCardClose = () => closeDebitCard(id);

    useEffect(() => {
        //if setLoading is not provided, return
        if (!setLoading) {
            return;
        }

        //if success is true and setSuccess is provided, set loading to false, set overlay error to null, and set success to true
        if (success && setSuccess) {
            setLoading(false);
            setOverlayError?.(null);
            setSuccess(true);
            return;
        }

        //if loading is true and setLoading is provided, set loading to true, set overlay error to null, and return
        if (loading) {
            setLoading(true);
            setOverlayError?.(null);
            setSuccess?.(false);
            return;
        }

        //if error is true and setOverlayError is provided, set loading to false, set overlay error to error message, and return
        if (error) {
            setLoading(false);
            setSuccess?.(false);
            setOverlayError?.(
                errorMessage ||
                    'Unable to close your card. Please try again.'
            );
            const resetId = window.setTimeout(() => {
                setOverlayError?.(null);
                resetDebitState();
            }, OVERLAY_ERROR_RESET_MS);
            return () => window.clearTimeout(resetId);
        }
    }, [
        error,
        errorMessage,
        setOverlayError,
        loading,
        setLoading, 
        success, 
        setSuccess,
        resetDebitState
    ]);

    return (
        <div className={classes.closeCardContainer}>
            <h2 className={classes.title}>
                Close {renderCardType(card_type)} Ending in {card_last4}
            </h2>
            <Card className={classes.cardStyle}>
                <b>This action cannot be undone</b>
                <br />
                <br />
                Are you sure you want to close your debit card?
            </Card>
            <div className={classes.buttonContainer}>
                <Button className={cancelButtonClass} onClick={onClose}>
                    Cancel
                </Button>
                <Button className={closeButtonClass} onClick={handleCardClose}>
                    Yes, Close Card
                </Button>
            </div>
        </div>
    );
};

export default CardClose;
